import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaGlobe } from 'react-icons/fa';
import '../styles/LoginForm.css';
import authService from '../services/authService';

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [token, setToken] = useState('');
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const languages = {
    'en': { name: 'English', flag: '/flags/en.png' },
    'cs': { name: 'Čeština', flag: '/flags/cs.png' }
  };

  // Extrahovat token z URL při prvním načtení komponenty
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tokenParam = queryParams.get('token');
    
    if (tokenParam) {
      setToken(tokenParam);
    } else {
      setMessage(t('resetPassword.errors.invalidLink'));
    }
  }, [location, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validace hesla
    if (password !== confirmPassword) {
      setMessage(t('resetPassword.errors.passwordsMismatch'));
      setIsSuccess(false);
      return;
    }

    if (password.length < 8) {
      setMessage(t('resetPassword.errors.passwordTooShort'));
      setIsSuccess(false);
      return;
    }

    setIsLoading(true);

    try {
      const response = await authService.resetPassword(token, password);
      setIsSuccess(true);
      setMessage(t('resetPassword.success'));
    } catch (error) {
      setIsSuccess(false);
      setMessage(error.response?.data || t('resetPassword.errors.general'));
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    setLanguageDropdownOpen(false);
  };

  const toggleLanguageDropdown = () => {
    setLanguageDropdownOpen(!languageDropdownOpen);
  };

  return (
    <div className="login-container">
      <div className="login-language-selector">
        <button
          onClick={toggleLanguageDropdown}
          className="language-toggle-btn"
          aria-label="Change language"
        >
          <FaGlobe />
        </button>
        {languageDropdownOpen && (
          <div className="language-dropdown">
            {Object.entries(languages).map(([code, { name, flag }]) => (
              <div
                key={code}
                onClick={() => handleLanguageChange(code)}
                className="language-option"
              >
                <img
                  src={flag}
                  alt={`${name} flag`}
                  className="language-flag"
                />
                {name}
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="login-card">
        <h2>Scoreo</h2>
        <h3>{t('resetPassword.title')}</h3>

        {!token && !isSuccess ? (
          <div className="error-message">{message}</div>
        ) : isSuccess ? (
          <div className="registration-success">
            <p className="success-message">{message}</p>
            <a href="/login" className="login-link">{t('resetPassword.goToLogin')}</a>
          </div>
        ) : (
          <>
            <p className="reset-instruction">
              {t('resetPassword.instruction')}
            </p>
            <form onSubmit={handleSubmit}>
              <div className="input-group">
                <label htmlFor="password">{t('resetPassword.newPassword')}</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength="8"
                />
              </div>
              <div className="input-group">
                <label htmlFor="confirmPassword">{t('resetPassword.confirmPassword')}</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength="8"
                />
              </div>
              <button 
                type="submit" 
                className="btn-primary"
                disabled={isLoading}
              >
                {isLoading ? t('resetPassword.submitting') : t('resetPassword.submit')}
              </button>
            </form>
            {message && <p className={isSuccess ? "success-message" : "error-message"}>{message}</p>}
            <p className="signup-link">
              {t('resetPassword.rememberedPassword')} <a href="/login">{t('resetPassword.login')}</a>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;